import React, {useState} from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {TextField, InputLabel, createStyles, Theme as AugmentedTheme, Dialog, DialogActions, Button} from '@material-ui/core';
import { CustomInputMappingField } from '@tuqqi/common';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { useSvgDrawing } from 'react-hooks-svgdrawing'
import { ClearIcon } from './ClearIcon';
import { dataUrlToFile, svgToPng } from './helpers';
import { uploadMultipleFilesMobileToBucket1 } from '../../../Attachment/uploader';
import { SetCaptcher } from '../../../../Main';

interface ISignature {
    policy?: string,
    signature?: string,
    field: CustomInputMappingField,
    value: string,
    onChangeHandler: (e: React.ChangeEvent<HTMLInputElement>) => void,
    isRtl: boolean
}

const useStyles = makeStyles((theme: AugmentedTheme) =>  createStyles({
    title: {
        marginTop: 16,
        marginLeft: 16,
        fontFamily: 'Arial',
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: 16,
        lineHeight: '24px',
        letterSpacing: 0.147638,
        color: '#0C0C0D',
    },
    body: {
        position: 'relative',
        margin: 16,
        height: 160
    } as CSSProperties,
    actions: {

    },
    paper: {
        width: 447,
        maxWidth: 447
    },
    draw: {
        width: 415,
        height: 160,
        background: '#FFFFFF',
        border: '1px solid #D7D7DB',
        borderRadius: 4,
        marginRight: 'auto', 
        marginLeft: 'auto'
    },
    clear: {
        top: 8,
        right: 8,
        position: 'absolute',
        cursor: 'pointer',
    } as CSSProperties,
    img: {
        marginTop: 12,
        boxSizing: 'border-box',
        width: 270,
        height: 104,
        background: '#FFFFFF',
        border: '1px solid #D7D7DB',
        borderRadius: 4,
    },
    noWrap: {
        display: 'contents'
    },
    requiredButton: {
        borderColor: '#d32f2f',
        borderStyle: 'dashed',
        borderWidth: 1,
    },
    hiddenReq: {
        height: 1,
        width: 1,
        marginRight: 'auto',
        marginLeft: '50%',
        display: 'flex',
        opacity: 0,
    },
    root: {
        zIndex: '3302!important' as any
    },
    field: {
        width: '100%',
        fontSize: '14px',
        fontWeight: 400,
        letterSpacing: '.2px',
        lineHeight: '20px',
        color: '#202124',

        "&::placeholder": {
            fontSize: '14px',
        },
    },
      //@ts-ignore
      inputLabel: theme.inputLabel,
      //@ts-ignore
      requiredLabel: theme.requiredLabel,
}))

type GetSvg = () => string | null

interface PropsDraw {
    classNameDraw: string, 
    classNameClear: string, 
    classNameBody: string, 
    classNameActions: string, 
    show: boolean, 
    onCancel: () => void
    onSave: (getSvg: GetSvg) => void
    // download: (s: string | null) => void
}

const emptySvg1 = '<svg width="415" height="160"></svg>'
const emptySvg2 = '<svg width="416.9965515136719" height="161.9965362548828"></svg>'

const DrawSignature = ({classNameClear, show, classNameDraw, classNameBody, classNameActions, onCancel, onSave}: PropsDraw) => {
    const [renderRef, draw] = useSvgDrawing({
        penWidth: 3,
    })
    const onClear = () => {
        draw.clear()
    }
    return (
        <>
        <div className={classNameBody}>
            <ClearIcon onClick={onClear} className={classNameClear} />
            <div className={classNameDraw} ref={renderRef} />
        </div>
        <DialogActions className={classNameActions}>
        <Button style={{textTransform: 'capitalize'}} color='primary' variant='text' onClick={onCancel} disabled={false}>
            Cancel
        </Button>
        <Button style={{textTransform: 'capitalize'}} color='primary' variant='text' onClick={() => onSave(() => draw.getSvgXML())} disabled={false}>
            Save
        </Button>
    </DialogActions>
    </>
    )
}

const getUrl = (value: string, url: string) => {
    if(!value || !url) return ''
    return url
}

export default function SignatureField({ field, value, onChangeHandler, isRtl }: ISignature) {
    const classes = useStyles();
    const [showModal, setShowModal] = useState(false)
    const [linkUrl, setLinkUrl] = useState('');
    const url = getUrl(value, linkUrl)
    
    const isEmpty = !value || !value
    const showRequired = field.required && isEmpty

    const openModal = () => {                
        setShowModal(true)
    }
    const closeModal = () => {        
        setShowModal(false)
    }

    const afterSave = (val: any | string) => {
        if(!val || !Object.keys(val).length) {
            onChangeHandler?.({ currentTarget: {value: ''} } as React.ChangeEvent<HTMLInputElement>)
            setShowModal(false)
            return
        }

        //@ts-ignore
        onChangeHandler?.({ currentTarget: {value: val} })
        setShowModal(false)
    }

    const onSave = async (getSvg: GetSvg) => {
        try {           
            const svgString = getSvg()
            
            if(!svgString || svgString === emptySvg1 || svgString === emptySvg2) {
                afterSave("")
                setLinkUrl("")
                return
            }
            const png = await svgToPng(svgString ?? '', 0, '#fff')
            const _file = await dataUrlToFile(png, 'signature-image-temp.png', 'image/png')
            const file = {
                fileName: _file.name,
                fileSize: _file.size,
                fileType: _file.type,
                uri: URL.createObjectURL(_file)
            }

            const abortController = new AbortController();

            try {
                const onSuccess = (res: any[]) => {
                    if(!res.length) {
                        afterSave("")
                        setLinkUrl("")
                        return
                    }
                    afterSave(res[0] ?? '')
                    setLinkUrl(file.uri)
                }
                const onError = () => {
                    afterSave("")
                    setLinkUrl("")
                }
                const captcha = await window.grecaptcha.execute("6LdZrBwcAAAAAOxKnSN_re2ZO6tkIJ-uM0Tej5kF", { action: "homepage" })
                SetCaptcher.cb(captcha)
                await uploadMultipleFilesMobileToBucket1(captcha)(
                    [file],
                    abortController,
                    onSuccess,
                    onError
                );

            } catch (error) {
                afterSave("")
                setLinkUrl("")   
            }
        } catch (error) {
            afterSave("")
            setLinkUrl("")      
        }
    }

    const Req =  field.required && (!url || !value) ? <TextField className={classes.hiddenReq} required style={{}} /> : null


    const NoValue =      <div className={classes.noWrap}>
    {/* <TextField  
            style={{ 
                direction: isRtl ? 'rtl' : 'ltr',
                textAlign: isRtl ? 'right' : 'left' 
            }} 
        disabled
        value={value} 
        id={field.sourceField} 
        className={classes.field} 
        placeholder={field.fieldName} 
        onChange={onChangeHandler} 
        name={field.fieldName} 
        type='text' 
        // required={field.required}                    
        required={false}                    
        inputProps={{dir: isRtl ? 'rtl' : 'ltr'}}
    /> */}
    {Req}
    <Button className={showRequired ? classes.requiredButton : ''} variant="outlined" onClick={openModal}>{field.fieldName}</Button>
    </div>

    const Value =      <>
        <InputLabel
    style={{ 
        direction: isRtl ? 'rtl' : 'ltr',
        textAlign: isRtl ? 'right' : 'left' 
    }} 
    htmlFor={field.sourceField} 
    className={showRequired ? classes.requiredLabel : classes.inputLabel}
    >{field.fieldName}</InputLabel>
    <img onClick={openModal} className={classes.img} src={url} alt="signature" />
    </>

    const Dial = <Dialog open={showModal} classes={{paper: classes.paper, root: classes.root}} dir='auto'>
        <div className={classes.title} >{"Signature field"}</div>
        <DrawSignature classNameClear={classes.clear} onCancel={closeModal} onSave={onSave} classNameActions={classes.actions} classNameBody={classes.body} classNameDraw={classes.draw} show={true} />
    </Dialog>

    return (
        <>
        

            {!url || !value ? NoValue : Value}
        
        {Dial}
        </>
    )
}